import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useUpdateGenericAccountMutation } from "../../../api/services/client/clientApis";
import {
  CreateGenericAccountParams,
  UpdateGenericAccountParams,
} from "../../../api/services/client/models";
import { useGetUsersDetailByRoleMutation } from "../../../api/services/user/userApi";
import {
  GENERIC_ACCOUNT_PURPOSE_LIST_SITE_CORE_VALUES,
  PAGE_URLS,
  ROLE_IDS,
} from "../../../helpers/constants";
import { getDateFormatByCountry } from "../../../helpers/constants/countryDateFormat";
import { RefDropdownHandle } from "../../../helpers/types/common";
import {
  convertToUTCFormat,
  getFieldUndefinedForPayloadParams,
  getValueOrBlank,
  isNotEmpty,
} from "../../../helpers/utils";
import useAccordion from "../../../hooks/useAccordion";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { useToast } from "../../../hooks/useToast";
import { toggleLoader } from "../../../redux/slices/appState/appStateSlice";
import { createGenericAccounts } from "../createGenericAccount/siteCore/SiteCoreKeys";
import { getMappedFeatures } from "../helper";
import { EditGenericAccountProps } from "./EditGenericAccount";

const initialAccordionStatus = {
  all: true,
  basicDetails: true,
  configuration: true,
};

export const useEditGenericAccountHook = (props: EditGenericAccountProps) => {
  const { genericAccountData, onApiSuccess, onApiError } = props;

  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { fetchLabel, commonLabels, fetchComponentTitle } = useSiteCore();

  const {
    createGenericAccountBasicDetailsTitle,
    createGenericAccountConfigurationTitle,
    createGenericAccountGenericAccountHeaderTitle,
    genericAccountDetailsUpdatedMessage,
    areYouSureYouWantToUpdateGenericAccountDetails,
  } = createGenericAccounts;

  const getHeaderLabel = (key: string) => {
    return fetchLabel(createGenericAccountGenericAccountHeaderTitle, key);
  };

  const navigate = useNavigate();
  const location = useLocation();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
  } = useAccordion(initialAccordionStatus);

  const deliveryOfficeRef = useRef<RefDropdownHandle>(null);

  const [showCancelConfirmationModal, setShowCancelConfirmationModal] =
    useState(false);
  const [showSaveUpdateConfirmationModal, setShowSaveUpdateConfirmationModal] =
    useState(false);

  const methods = useFormContext();
  const { reset, getValues, handleSubmit, formState } = methods;

  const genericAccountId = useMemo(
    () => genericAccountData?.id,
    [genericAccountData],
  );
  const from = location?.state?.from;

  const {
    mutateAsync: getUsersDetailsByRole,
    data: usersList,
    isPending: isUsersListPending,
    isError: isUsersListError,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: updateGenericAccount,
    isPending: isUpdateGenericAccountPending,
    isSuccess: isUpdateGenericAccountSuccess,
    isError: isUpdateGenericAccountError,
  } = useUpdateGenericAccountMutation();

  useEffect(() => {
    getUsersDetailsByRole({
      roleIds: [
        ROLE_IDS.sales,
        ROLE_IDS.admin,
        ROLE_IDS.corporateAdmin,
        ROLE_IDS.outreachConsultantLead,
        ROLE_IDS.projectManager,
        ROLE_IDS.projectCoOrdinator,
      ],
    });
  }, []);

  useEffect(() => {
    if (isUsersListError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isUsersListError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isUsersListPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isUsersListPending]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isUpdateGenericAccountPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isUpdateGenericAccountPending]);

  useEffect(() => {
    if (isUpdateGenericAccountSuccess) {
      displayToast(
        getHeaderLabel(genericAccountDetailsUpdatedMessage),
        "success",
      );
      reset(getValues());
      onApiSuccess?.();
    }
  }, [isUpdateGenericAccountSuccess]);

  useEffect(() => {
    if (isUpdateGenericAccountError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      onApiError?.();
    }
  }, [isUpdateGenericAccountError]);

  const navigateToViewGenericAccount = (accountId?: string) => {
    navigate(PAGE_URLS.VIEW_GENERIC_ACCOUNT, {
      replace: true,
      state: {
        genericAccountId: accountId ?? genericAccountId,
        from: from,
      },
    });
  };

  const navigateBack = () => {
    if (from) {
      navigateToViewGenericAccount();
    } else {
      navigate(-1);
    }
  };

  const handleCancelModalPrimaryButtonClick = () => {
    setShowCancelConfirmationModal(false);
    navigateBack();
  };

  const handleCancelClick = () => {
    if (!!isNotEmpty(formState.touchedFields)) {
      setShowCancelConfirmationModal(true);
      return;
    }
    navigateBack();
  };

  const checkErrorsAndShowModal = () => {
    if (isNotEmpty(formState.errors)) {
      if (
        formState.errors.genericAccountName ||
        formState.errors.genericRegistrationCode
      ) {
        return;
      }
      handleSaveModalPrimaryButtonClick();
      return;
    }
    setShowSaveUpdateConfirmationModal(true);
  };

  const handleSaveModalPrimaryButtonClick = () => {
    setShowSaveUpdateConfirmationModal(false);
    handleSubmit(callUpdateGenericAccountAPI)();
  };

  const handleSubmitClick = () => {
    checkErrorsAndShowModal();
  };

  const getEditGenericAccountParams = () => {
    const {
      genericAccountName,
      purpose,
      clientId,
      genericRegistrationCode,
      startDate,
      expirationDate,
      numberOfAccounts,
      programDuration,
      requestedBy,
      nextView,
      country,
      deliveryOffice,
      nextProgramClass,
      features,
      status,
    } = getValues();

    const updatedFeatures = getMappedFeatures(features);

    let matchedUser;
    if (requestedBy && isNotEmpty(requestedBy)) {
      matchedUser = genericAccountData?.requestedByUserId;
    }

    const params: CreateGenericAccountParams = {
      name: genericAccountName,
      purposeId: purpose,
      ...(purpose !== GENERIC_ACCOUNT_PURPOSE_LIST_SITE_CORE_VALUES.TEST_ACCESS
        ? getFieldUndefinedForPayloadParams({ clientId })
        : {}),
      registrationCode: genericRegistrationCode,
      startDate: convertToUTCFormat(startDate, userCountryDateFormat),
      expirationDate: convertToUTCFormat(
        expirationDate,
        userCountryDateFormat,
        true,
      ),
      numberOfAccounts,
      programDuration,
      ...(requestedBy && {
        requestedBy,
        requestedByUserId: getValueOrBlank(matchedUser?.id),
      }),
      solutionOfferingId: nextView,
      countryId: country,
      deliveryOfficeIds: deliveryOffice,
      programClassId: nextProgramClass,
      status: status,
      features: updatedFeatures,
    };

    return params;
  };

  const callUpdateGenericAccountAPI = () => {
    const editGenericAccountParams = getEditGenericAccountParams();

    const params: UpdateGenericAccountParams = {
      ...editGenericAccountParams,
      genericAccountId: getValueOrBlank(genericAccountId),
    };
    updateGenericAccount(params);
  };

  const handleUpdateClick = () => {
    checkErrorsAndShowModal();
  };

  const handleCancelModalClose = () => {
    if (showCancelConfirmationModal || showSaveUpdateConfirmationModal) {
      setShowCancelConfirmationModal(false);
      setShowSaveUpdateConfirmationModal(false);
    }
  };

  const getConfirmationLabel = () =>
    getHeaderLabel(areYouSureYouWantToUpdateGenericAccountDetails);

  return {
    // hooks
    deliveryOfficeRef,
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
    // state
    showCancelConfirmationModal,
    showSaveUpdateConfirmationModal,
    // handlers
    handleCancelClick,
    handleSubmitClick,
    handleCancelModalPrimaryButtonClick,
    handleCancelModalClose,
    handleSaveModalPrimaryButtonClick,
    handleUpdateClick,
    // data
    usersList,
    commonLabels,
    createGenericAccounts,
    fetchComponentTitle,
    fetchLabel,
    //labels
    genericAccountBasicDetailsTitle: fetchComponentTitle(
      createGenericAccountBasicDetailsTitle,
    ),
    genericAccountConfigurationTitle: fetchComponentTitle(
      createGenericAccountConfigurationTitle,
    ),
    confirmationToCreateGenericAccountLabel: getConfirmationLabel(),
  };
};
